import { Button, Skeleton } from 'antd';
import type { BtlApplication } from 'api/btlApplicationForm/types';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { CaseSidebarHeader } from './CaseSidebarHeader';
import { CaseSidebarNavItem } from './CaseSidebarNavItem';
import { CaseSidebarSectionGroup } from './CaseSidebarSectionGroup';
import type { CaseSectionItem, CaseSidebarState } from '../types';
import { CaseSidebarItemId } from '../types';

export interface CaseSidebarProps {
  overviewItem: CaseSectionItem;
  state: CaseSidebarState;
  activeItemId?: CaseSidebarItemId;
  onItemSelect?: (itemId: CaseSidebarItemId) => void;
  headerTitle?: string;
  referenceNumber?: string;
  statusLabel?: string;
  application?: BtlApplication;
  isLoading?: boolean;
  footer?: ReactNode;
  showDisableTopSlicingButton?: boolean;
  onDisableTopSlicing?: () => void;
  showOverviewItem?: boolean;
}

export function CaseSidebar({
  overviewItem,
  state,
  activeItemId,
  onItemSelect,
  headerTitle,
  referenceNumber,
  statusLabel,
  application,
  isLoading = false,
  footer,
  showDisableTopSlicingButton = false,
  onDisableTopSlicing,
  showOverviewItem = true,
}: CaseSidebarProps) {
  const { formatMessage } = useIntl();
  const hasFooterContent = Boolean(footer) || (showDisableTopSlicingButton && onDisableTopSlicing);

  return (
    <aside
      className='flex h-full min-h-0 w-[248px] shrink-0 flex-col rounded-xl bg-white p-4'
      data-testid='case-sidebar'
    >
      {headerTitle && (
        <CaseSidebarHeader
          title={headerTitle}
          referenceNumber={referenceNumber}
          statusLabel={statusLabel}
          isLoading={isLoading}
        />
      )}

      <div className='custom-scrollbar min-h-0 flex-1 overflow-y-auto'>
        <Skeleton loading={isLoading} active paragraph={{ rows: 8 }}>
          <div className='flex flex-col gap-1'>
            {showOverviewItem && (
              <div>
                <CaseSidebarNavItem
                  item={overviewItem}
                  application={application}
                  isActive={activeItemId === overviewItem.id}
                  onClick={onItemSelect ? () => onItemSelect(overviewItem.id) : undefined}
                />
              </div>
            )}

            {state.sections.map(section => (
              <CaseSidebarSectionGroup
                key={section.title}
                section={section}
                activeItemId={activeItemId}
                application={application}
                onItemSelect={onItemSelect}
              />
            ))}
          </div>
        </Skeleton>
      </div>

      {hasFooterContent && (
        <div className='mt-4 shrink-0'>
          <div className='mb-4 h-px w-full bg-gray-200' role='presentation' />
          <Skeleton loading={isLoading} active paragraph={{ rows: 1 }}>
            {showDisableTopSlicingButton && onDisableTopSlicing && (
              <Button
                className='w-full border-gray-200 bg-white shadow-none'
                size='middle'
                color='default'
                variant='outlined'
                onClick={onDisableTopSlicing}
              >
                {formatMessage({ id: 'caseSidebar.actions.disableTopSlicing' })}
              </Button>
            )}
            {footer}
          </Skeleton>
        </div>
      )}
    </aside>
  );
}
